import { useModal } from 'mui-modal-provider';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { useBind } from '../../../../../hooks/use-bind/use-bind';
import { DELETE_KIDS_PROFILE } from '../../../../../graphql/profile/mutations';
import { useActionsInProgress } from '../../../../../graphql/preloader/actions/actions-in-progress';
import { useCancellablePromise } from '../../../../../hooks/use-cancellable-promise/use-cancellable-promise';
import { InternalLinks } from '../../../../../constants';
import { Button } from '../../../../common/button-new';
import { GET_DEPENDENT_ORDERS } from '../../../../../graphql/order/queries/get-dependent-orders';
import { ErrorModal } from '../error-modal';
import { DependentProfile } from '../../../../../graphql/profile/models/kids.models';
import { GET_DEPENDENTS } from '../../../../../graphql/profile/queries/get-dependents';

export const DeleteKidsProfile = ({ id }: { id: string }): JSX.Element => {
  const { data } = useQuery(GET_DEPENDENT_ORDERS, { fetchPolicy: 'network-only', variables: { id } });
  const { data: kidsData } = useQuery<{ getDependents: DependentProfile[] }>(GET_DEPENDENTS, { fetchPolicy: 'network-only' });

  const [deleteDependent, deleteDependentMutationSettings] = useMutation(DELETE_KIDS_PROFILE);
  const { loading } = deleteDependentMutationSettings;
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();

  const loadingBind = useBind(loading);
  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const updateDependentBind = useBind(deleteDependent);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);
  const navigate = useNavigate();
  const { showModal } = useModal();

  const handleError = (): void => {
    showModal(ErrorModal, {
      title: 'deleteKidsProfile.error-3.title',
      text: 'createKidsProfile.error.text',
      firstButton: 'createKidsProfile.error.button',
    });
  };

  const handleDelete = useCallback(async () => {
    addActionInProgressBind.current();

    try {
      removeActionInProgressBind.current();
      addActionInProgressBind.current();

      try {
        await makeCancellablePromiseBind.current(
          updateDependentBind.current({ variables: { id } }),
        );

        removeActionInProgressBind.current();

        if ((kidsData?.getDependents?.length as number) > 1) {
          navigate(InternalLinks.forKids);
        } else {
          navigate(InternalLinks.home);
        }

      } catch (error: any) {
        removeActionInProgressBind.current();

        handleError();

        if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
          return;
        }
      }
    } catch {
      removeActionInProgressBind.current();

      handleError();
    }
  }, [
    kidsData,
    loadingBind,
    makeCancellablePromiseBind,
    CancelledPromiseOnUnmountErrorBind,
    updateDependentBind,
    addActionInProgressBind,
    removeActionInProgressBind,
  ]);

  const handleDeleteConfirm = useCallback(async () => {
    if (loadingBind.current) {
      return;
    }

    if (data?.getActiveDependentOrdersById?.result) {
      showModal(ErrorModal, {
        title: 'deleteKidsProfile.error-1.title',
        text: 'deleteKidsProfile.error-1.text',
        firstButton: 'deleteKidsProfile.seeOrder',
        secondButton: 'deleteKidsProfile.confirmDelete',
        onSecondButtonClick: handleDelete,
        onFirstButtonClick: () => navigate(InternalLinks.myOrdersPage),
      });
    } else {
      showModal(ErrorModal, {
        title: 'deleteKidsProfile.error-1.title',
        text: 'deleteKidsProfile.error-2.text',
        firstButton: 'deleteKidsProfile.confirmDelete',
        secondButton: 'common.cancelButton.label',
        onFirstButtonClick: handleDelete,
      });
    }
  }, [
    loadingBind,
    data,
  ]);

  return (
    <Button style="outlined" onClick={handleDeleteConfirm} type="button">
      <FormattedMessage id="profile.deleteProfileButton.label" />
    </Button>
  );
};
